import isEmpty from "lodash/isEmpty";
import {
  REACT_APP_AUTH_URL,
  REDIRECT_API_PATH,
  REDIRECT_PENDING_STORAGE_KEY,
  REDIRECT_TX_COOKIE_NAME,
} from "../../../../../constants/env";
import type { IApiClientService } from "../../../../../legacy/services/ApiClient/ApiClientService.interface";
import AxiosApiClientInstance from "../../../../../legacy/services/AxiosApiClientInstance/AxiosApiClientInstance";
import { getCookie } from "../../../../../legacy/utils/getCookie";
import type { ICachedRedirectData, IPendingRedirect } from "../../types/next-url-redirect.type";
import type { INextUrlRedirectService } from "./next-url-redirect.service.interface";

const MAX_CACHED_REDIRECTS = 20;

class NextUrlRedirectService implements INextUrlRedirectService {
  private readonly _STORAGE_KEY = REDIRECT_PENDING_STORAGE_KEY;
  private readonly _API_PATH = REDIRECT_API_PATH;

  constructor(
    private readonly _client: IApiClientService = new AxiosApiClientInstance({
      baseURL: REACT_APP_AUTH_URL as string,
      withCredentials: true,
    })
  ) {}

  async getPendingRedirects(): Promise<IPendingRedirect[] | null> {
    const nextUrlId = this._getNextUrlId();
    if (!nextUrlId) {
      return null;
    }

    if (this._isCacheValid(nextUrlId)) {
      const cached = this._getFromStorage();
      const redirects = cached?.redirects;
      if (redirects) {
        return redirects;
      }
    }

    const redirects = await this._fetchFromApi();
    const hasRedirects = !isEmpty(redirects);
    if (!hasRedirects) {
      return null;
    }

    this._saveToStorage(nextUrlId, redirects);

    return redirects;
  }

  async clearPendingRedirect(triggerRoute: string): Promise<void> {
    try {
      await this._deleteFromApi(triggerRoute);
      this._removeFromStorage(triggerRoute);
    } catch (error) {
      console.error("Failed to clear pending redirect:", error);
    }
  }

  private async _deleteFromApi(triggerRoute: string): Promise<void> {
    const encodedRoute = encodeURIComponent(triggerRoute);
    await this._client.delete(`${this._API_PATH}/${encodedRoute}`, {});
  }

  private _removeFromStorage(triggerRoute: string): void {
    try {
      const cached = this._getFromStorage();
      if (!cached) {
        return;
      }

      const updatedRedirects = cached.redirects.filter(function filterRedirect(redirect) {
        return redirect.triggerRoute !== triggerRoute;
      });

      const hasRedirects = !isEmpty(updatedRedirects);
      if (!hasRedirects) {
        localStorage.removeItem(this._STORAGE_KEY);
        return;
      }

      const updatedData: ICachedRedirectData = {
        nextUrlId: cached.nextUrlId,
        redirects: updatedRedirects,
      };
      localStorage.setItem(this._STORAGE_KEY, JSON.stringify(updatedData));
    } catch (error) {
      console.warn("Failed to update redirect data in localStorage:", error);
    }
  }

  private _getNextUrlId(): string | undefined {
    const cookie = getCookie(REDIRECT_TX_COOKIE_NAME);
    return cookie;
  }

  private _isCacheValid(nextUrlId: string): boolean {
    const cached = this._getFromStorage();
    if (!cached) {
      return false;
    }

    const isCacheValid = cached.nextUrlId === nextUrlId;
    return isCacheValid;
  }

  private _getFromStorage(): ICachedRedirectData | null {
    try {
      const stored = localStorage.getItem(this._STORAGE_KEY);
      if (!stored) {
        return null;
      }
      const data = JSON.parse(stored) as ICachedRedirectData;
      return data;
    } catch (error) {
      console.error("Failed to parse cached redirect data:", error);
      return null;
    }
  }

  private _saveToStorage(nextUrlId: string, redirects: IPendingRedirect[]): void {
    try {
      const data: ICachedRedirectData = { nextUrlId, redirects: redirects.slice(0, MAX_CACHED_REDIRECTS) };
      localStorage.setItem(this._STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Failed to save redirect data to localStorage:", error);
    }
  }

  private async _fetchFromApi(): Promise<IPendingRedirect[]> {
    try {
      const response = await this._client.get<IPendingRedirect[]>(this._API_PATH, {});
      const redirects = response.data;
      const hasRedirects = redirects && Array.isArray(redirects);
      if (!hasRedirects) {
        return [];
      }

      return redirects;
    } catch (error) {
      console.error("Failed to fetch pending redirects:", error);
      return [];
    }
  }
}

export { NextUrlRedirectService };
